<template>
  <v-container fluid>
    <v-progress-linear indeterminate v-if="loading"/>
    <v-card-actions>
      <v-btn x-large icon title="Previous production" @click="stepProduction(-1)">
        <v-icon x-large>mdi-chevron-left</v-icon>
      </v-btn>
      <v-chip outlined label class="mx-1" style="font-size: 1rem">{{ productionLabel }}</v-chip>
      <v-btn x-large icon title="Next production" @click="stepProduction(1)">
        <v-icon x-large>mdi-chevron-right</v-icon>
      </v-btn>
      <div style="min-width: 200px" class="px-4">
        <DatePicker :date="selectedDate" v-on:update:date="selectedDate=$event"/>
      </div>
      <v-spacer/>
      <v-btn outlined :disabled="componentRows.length===0" @click="downloadCsv">Download CSV</v-btn>
    </v-card-actions>
    <v-card-text v-if="!loading && meals.length>0" class="pb-0">
      <v-alert type="info" outlined dense>
        {{ meals.length }} meals across {{ effectiveDates.length }} dates use
        {{ componentRowsUnfiltered.length }} components in {{ dietsPresent.length }} diets.
        {{ singleDietCount }} components are used by only one diet.
      </v-alert>
      <v-chip-group column>
        <v-chip
          v-for="diet of dietSummary" v-bind:key="diet.id"
          outlined
          :color="dietFilter===diet.id?'primary':''"
          @click="toggleDietFilter(diet.id)"
        >
          {{ diet.name }}: uses {{ diet.componentCount }}, exclusive {{ diet.exclusiveCount }}
        </v-chip>
      </v-chip-group>
    </v-card-text>
    <v-card-actions>
      <v-text-field
        v-model="searchInput"
        append-icon="mdi-magnify"
        label="Search components or meals"
        single-line
        hide-details
        clearable
        style="max-width: 300px"
      />
      <v-select
        class="ml-4"
        v-model="dietFilter"
        :items="dietSelectItems"
        label="Diet"
        clearable
        hide-details
        style="max-width: 250px"
      />
      <v-checkbox
        class="ml-4"
        v-model="exclusiveOnly"
        :disabled="!dietFilter"
        label="Only exclusive to selected diet"
        hide-details
      />
      <v-checkbox
        class="ml-4"
        v-model="singleDietOnly"
        label="Single-diet components only"
        hide-details
      />
    </v-card-actions>
    <v-data-table
      :headers="headers"
      :items="componentRows"
      item-key="id"
      show-expand
      :expanded.sync="expanded"
      sort-by="name"
      :footer-props="{itemsPerPageOptions:[25,50,100,-1]}"
      @click:row="toggleExpand"
    >
      <template v-slot:item.name="{item}">
        <router-link
          class="mr-1"
          :to="{name:'ComponentDetail', params:{id:item.id}}"
          @click.native.stop
        >
          C{{ item.id }}
        </router-link>
        <span v-if="item.cost100.hasAnyPrice" class="mr-1 grey--text text--darken-1">
          ${{ formatDollars(item.cost100.cost) }}
        </span>
        <span style="cursor: pointer">{{ item.name }}</span>
      </template>
      <template v-slot:item.dietNames="{item}">
        <v-chip class="mr-1" x-small outlined v-for="name of item.dietNames" v-bind:key="name">{{ name }}</v-chip>
      </template>
      <template v-slot:expanded-item="{headers: expandedHeaders, item}">
        <td :colspan="expandedHeaders.length" class="py-3">
          <v-row>
            <v-col cols="3">
              <div>
                <strong class="mr-2">Default ingredients</strong>
                {{ item.name }}
              </div>
              <div class="caption">a 100g portion consists of</div>
              <ul v-if="item.default100.length>0">
                <li v-for="ingredient of item.default100" v-bind:key="ingredient.name">
                  {{ ingredient.amount }}g {{ ingredient.name }}
                </li>
              </ul>
              <v-alert v-else dense outlined type="warning" class="mt-2">No default component</v-alert>
              <div v-if="item.defaultCost.hasAnyPrice">
                cost: <b>${{ formatDollars(item.defaultCost.cost) }}</b> per 100g
              </div>
              <div v-if="item.defaultCost.unpriced.length>0" class="caption orange--text">
                no price for {{ item.defaultCost.unpriced.map(i => i.name).join(', ') }}
              </div>
              <v-btn
                v-if="hasNonDefaultVariant(item)"
                small outlined class="mt-2"
                @click="openDefaultDialog(item)"
              >
                <v-icon small left>mdi-star</v-icon>
                Replace Default
              </v-btn>
            </v-col>
            <v-col cols="9">
              <template v-for="group of item.mealsByDiet">
                <div
                  v-for="entry of group.entries" v-bind:key="`${group.dietId}-${entry.meal.id}`"
                  class="meal-entry mb-3"
                >
                  <div>
                    <strong class="mr-2">{{ group.dietName }}</strong>
                    {{ entry.meal.name }} ({{ formatMealDay(entry.meal.date) }})
                    <b v-if="entry.totalAmount>0" class="ml-1">{{ formatWeightWithUnits(entry.totalAmount) }}</b>
                    <span v-if="entry.cost.hasAnyPrice" class="ml-1">(${{ formatDollars(entry.cost.cost) }})</span>
                    <span v-if="entry.cost.unpriced.length>0" class="caption orange--text ml-1">
                      no price for {{ entry.cost.unpriced.map(i => i.name).join(', ') }}
                    </span>
                    <v-chip v-if="entry.isDefault" small outlined color="green" class="ml-2">using default</v-chip>
                  </div>
                  <div class="mt-1">
                    <v-btn
                      small outlined class="mr-2"
                      @click="openMealDialog(entry.meal, group.dietName)"
                    >
                      <v-icon small left>mdi-eye</v-icon>
                      Show Meal
                    </v-btn>
                    <v-btn
                      small outlined
                      @click="openReplaceDialog(item, entry)"
                    >
                      <v-icon small left>mdi-swap-horizontal</v-icon>
                      Replace Component
                    </v-btn>
                  </div>
                  <span v-if="!entry.isDefault && entry.variant.length===0" class="caption">no weighed ingredients</span>
                  <ul v-if="!entry.isDefault && entry.variant.length>0" class="mt-1">
                    <li
                      v-for="ingredient of entry.comparison.items" v-bind:key="ingredient.name"
                      :style="ingredient.changed ? 'font-weight: bolder' : ''"
                    >
                      {{ ingredient.amount }}g {{ ingredient.name }}
                    </li>
                    <li v-for="ingredient of entry.comparison.removed" v-bind:key="`removed-${ingredient.name}`">
                      <span class="text-decoration-line-through red--text">{{ ingredient.name }}</span>
                    </li>
                  </ul>
                  <div v-if="!entry.isDefault && entry.variant.length>0 && entry.cost100.hasAnyPrice">
                    cost: <b>${{ formatDollars(entry.cost100.cost) }}</b> per 100g
                  </div>
                </div>
              </template>
            </v-col>
          </v-row>
        </td>
      </template>
    </v-data-table>
    <MealSummaryDialog
      v-model="showMealDialog"
      :meal-id="mealDialogTarget.mealId"
      :meal-name="mealDialogTarget.mealName"
      :diet-name="mealDialogTarget.dietName"
      v-on:renamed="refetchUsage"
      v-on:restored="refetchUsage"
    />
    <ReplaceDefaultDialog
      v-model="showDefaultDialog"
      :component-id="defaultDialogTarget.componentId"
      :component-name="defaultDialogTarget.componentName"
      v-on:replaced="refetchUsage"
    />
    <ComponentReplaceDialog
      v-model="showReplaceDialog"
      :meal-id="replaceTarget.mealId"
      :meal-name="replaceTarget.mealName"
      :meal-date="replaceTarget.mealDate"
      :meal-sku="replaceTarget.mealSku"
      :component-id="replaceTarget.componentId"
      :component-name="replaceTarget.componentName"
      :component-notes="replaceTarget.componentNotes"
      :current-ingredients="replaceTarget.currentIngredients || []"
      :candidates="replaceCandidates"
      :edit-allowed="isEditAllowed"
      v-on:replaced="refetchUsage"
    />
  </v-container>
</template>

<script>
import {mapActions, mapGetters} from 'vuex';
import moment from 'moment';
import api from '@/api';
import urlState from '@/router/urlState';
import {csvExport, formatWeightWithUnits, getNextOrderingDate, getProductionDays} from '@/store/utils';
import DatePicker from '@/components/DatePicker.vue';
import ComponentReplaceDialog from '@/components/ComponentReplaceDialog.vue';
import MealSummaryDialog from '@/components/MealSummaryDialog.vue';
import ReplaceDefaultDialog from '@/components/ReplaceDefaultDialog.vue';
import {compareIngredients, formatDollars, ingredientCost, ingredientsMatch, normaliseTo100g} from '@/utils/componentIngredients';

export default {
  name: 'ComponentUsage',
  components: {DatePicker, ComponentReplaceDialog, MealSummaryDialog, ReplaceDefaultDialog},
  mixins: [urlState],
  data() {
    return {
      loading: null,
      selectedDate: moment().format(moment.HTML5_FMT.DATE),
      meals: [],
      components: [],
      fetchedDatesKey: null,
      searchInput: '',
      dietFilter: null,
      exclusiveOnly: false,
      singleDietOnly: false,
      expanded: [],
      showDefaultDialog: false,
      defaultDialogTarget: {},
      showReplaceDialog: false,
      replaceTarget: {},
      replaceCandidates: [],
      showMealDialog: false,
      mealDialogTarget: {},
    };
  },
  mounted() {
    this.syncToUrl({
      param: 'selectedDate', urlParam: 'date', initFromRoute: true,
    });
    this.syncToUrl({
      param: 'searchInput', urlParam: 'search', initFromRoute: true,
    });
    this.syncToUrl({
      param: 'dietFilter', urlParam: 'diet', initFromRoute: true,
      parseCallback: (v) => v ? Number(v) : null
    });
    this.syncToUrl({
      param: 'exclusiveOnly', urlParam: 'exclusive', initFromRoute: true,
      parseCallback: (v) => v === 'true'
    });
    this.syncToUrl({
      param: 'singleDietOnly', urlParam: 'single', initFromRoute: true,
      parseCallback: (v) => v === 'true'
    });
    this.fetchDiets();
    this.$nextTick(() => this.fetchUsage());
  },
  watch: {
    effectiveDatesKey() {
      return this.fetchUsage();
    },
    dietFilter(v) {
      if (!v) {
        this.exclusiveOnly = false;
      }
    }
  },
  computed: {
    ...mapGetters(['getDiet']),
    effectiveDates() {
      if (!this.selectedDate) {
        return [];
      }
      // the production block the selected date is in; Sunday has no production,
      // so it resolves forward to the next block
      const date = moment(this.selectedDate).day() === 0
        ? moment(this.selectedDate).add(1, 'day').format(moment.HTML5_FMT.DATE)
        : this.selectedDate;
      return getProductionDays(date);
    },
    effectiveDatesKey() {
      return this.effectiveDates.join(',');
    },
    productionLabel() {
      const days = this.effectiveDates;
      if (days.length === 0) {
        return 'no production selected';
      }
      return `${moment(days[0]).format('ddd MMM D')} - ${moment(days[days.length - 1]).format('ddd MMM D')}`;
    },
    dietsPresent() {
      return [...new Set(this.meals.map(m => m.diet))]
        .map(id => ({id, name: this.getDiet(id).name || `diet ${id}`}))
        .sort((a, b) => a.name.localeCompare(b.name));
    },
    dietSelectItems() {
      return this.dietsPresent.map(d => ({value: d.id, text: d.name}));
    },
    componentRowsUnfiltered() {
      const componentMap = Object.fromEntries(this.components.map(c => [c.id, c]));
      const usage = {};
      this.meals.forEach(meal => {
        meal.component_ids.forEach(id => {
          usage[id] = usage[id] || {meals: [], dietIds: new Set()};
          usage[id].meals.push(meal);
          usage[id].dietIds.add(meal.diet);
        });
      });
      return Object.entries(usage).map(([id, {meals, dietIds}]) => {
        const componentId = Number(id);
        const component = componentMap[id] || {};
        const default100 = normaliseTo100g(component.default_ingredients || []);
        const makeEntry = meal => {
          const variant = (meal.ingredients || []).filter(i => i.component_id === componentId);
          const variant100 = normaliseTo100g(variant);
          const isDefault = default100.length > 0 && variant.length > 0
            && ingredientsMatch(variant100, default100);
          const comparison = default100.length > 0
            ? compareIngredients(variant100, default100)
            : {items: variant100.map(i => ({...i, changed: false})), removed: []};
          const totalAmount = variant.reduce((sum, i) => sum + i.amount, 0);
          const cost = ingredientCost(variant);
          const cost100 = ingredientCost(variant100);
          return {meal, variant, variant100, isDefault, comparison, totalAmount, cost, cost100};
        };
        const dietList = [...dietIds];
        const mealsByDiet = dietList
          .map(dietId => ({
            dietId,
            dietName: this.getDiet(dietId).name || `diet ${dietId}`,
            entries: meals
              .filter(m => m.diet === dietId)
              .sort((a, b) => `${a.date}${a.name}`.localeCompare(`${b.date}${b.name}`))
              .map(makeEntry)
          }))
          .sort((a, b) => a.dietName.localeCompare(b.dietName));
        // the version a replacement copies: prefer a meal on the default recipe, else any meal version
        const allEntries = mealsByDiet.flatMap(g => g.entries);
        const source = allEntries.find(e => e.isDefault)
          || allEntries.find(e => e.variant.length > 0)
          || null;
        const defaultCost = ingredientCost(default100);
        // list cost: the default recipe when there is one, else the version a replacement would copy
        const cost100 = default100.length > 0
          ? defaultCost
          : ingredientCost(source ? source.variant100 : []);
        return {
          id: componentId,
          name: component.name || `component ${id}`,
          category: component.category || '',
          notes: component.notes || '',
          dietIds: dietList,
          dietCount: dietList.length,
          dietNames: mealsByDiet.map(g => g.dietName),
          mealCount: meals.length,
          mealsByDiet,
          default100,
          defaultCost,
          cost100,
          source,
        };
      });
    },
    componentRows() {
      let rows = this.componentRowsUnfiltered;
      if (this.dietFilter) {
        rows = rows.filter(r => r.dietIds.includes(this.dietFilter));
        if (this.exclusiveOnly) {
          rows = rows.filter(r => r.dietCount === 1);
        }
      }
      if (this.singleDietOnly) {
        rows = rows.filter(r => r.dietCount === 1);
      }
      if (this.searchInput) {
        const search = this.searchInput.toLowerCase();
        rows = rows.filter(r =>
          r.name.toLowerCase().includes(search)
          || r.mealsByDiet.some(group =>
            group.entries.some(entry => `${entry.meal.name}`.toLowerCase().includes(search))));
      }
      return rows;
    },
    singleDietCount() {
      return this.componentRowsUnfiltered.filter(r => r.dietCount === 1).length;
    },
    // same edit-cutoff rule as MealAudit: meals near/past their production date are
    // locked because customer orders for them may already exist
    isEditAllowed() {
      const dateTo = this.effectiveDates[this.effectiveDates.length - 1];
      const cutoffEditDate = moment(getNextOrderingDate()).add(1, 'week');
      return !!dateTo && cutoffEditDate.isBefore(dateTo);
    },
    dietSummary() {
      return this.dietsPresent.map(diet => ({
        ...diet,
        componentCount: this.componentRowsUnfiltered.filter(r => r.dietIds.includes(diet.id)).length,
        exclusiveCount: this.componentRowsUnfiltered
          .filter(r => r.dietCount === 1 && r.dietIds[0] === diet.id).length,
      }));
    },
    headers() {
      return [
        {text: 'Component', value: 'name'},
        {text: 'Category', value: 'category'},
        {text: '# Diets', value: 'dietCount'},
        {text: 'Diets', value: 'dietNames', sortable: false},
        {text: '# Meals', value: 'mealCount'},
        {text: '', value: 'data-table-expand'},
      ];
    },
  },
  methods: {
    ...mapActions(['fetchDiets']),
    toggleDietFilter(dietId) {
      this.dietFilter = this.dietFilter === dietId ? null : dietId;
    },
    stepProduction(direction) {
      const days = this.effectiveDates;
      if (days.length === 0) {
        this.selectedDate = moment().format(moment.HTML5_FMT.DATE);
        return;
      }
      let date = direction > 0
        ? moment(days[days.length - 1]).add(1, 'day')
        : moment(days[0]).subtract(1, 'day');
      if (date.day() === 0) {
        // skip sunday to reach the neighbouring production block
        date = direction > 0 ? date.add(1, 'day') : date.subtract(1, 'day');
      }
      this.selectedDate = date.format(moment.HTML5_FMT.DATE);
    },
    openReplaceDialog(componentRow, entry) {
      const meal = entry.meal;
      // candidates must come from a meal in this production, and replacing with a
      // component the meal already uses would silently merge the two - exclude those
      this.replaceCandidates = this.componentRowsUnfiltered
        .filter(r => r.id !== componentRow.id && r.source && !meal.component_ids.includes(r.id))
        .map(r => ({
          id: r.id,
          name: r.name,
          notes: r.notes,
          sourceMealId: r.source.meal.id,
          sourceMealName: r.source.meal.name,
          sourceIsDefault: r.source.isDefault,
          ingredients: r.source.variant,
        }));
      this.replaceTarget = {
        mealId: meal.id,
        mealName: meal.name,
        mealDate: meal.date,
        mealSku: meal.sku || null,
        componentId: componentRow.id,
        componentName: componentRow.name,
        componentNotes: componentRow.notes,
        currentIngredients: entry.variant,
      };
      this.showReplaceDialog = true;
    },
    hasNonDefaultVariant(componentRow) {
      return componentRow.mealsByDiet
        .some(group => group.entries.some(entry => entry.variant.length > 0 && !entry.isDefault));
    },
    openDefaultDialog(componentRow) {
      this.defaultDialogTarget = {
        componentId: componentRow.id,
        componentName: componentRow.name,
      };
      this.showDefaultDialog = true;
    },
    openMealDialog(meal, dietName) {
      this.mealDialogTarget = {
        mealId: meal.id,
        mealName: meal.name,
        dietName,
      };
      this.showMealDialog = true;
    },
    toggleExpand(item) {
      if (this.expanded.includes(item)) {
        this.expanded = this.expanded.filter(i => i !== item);
      } else {
        this.expanded = [...this.expanded, item];
      }
    },
    refetchUsage() {
      this.fetchedDatesKey = null;
      return this.fetchUsage();
    },
    formatWeightWithUnits,
    formatDollars,
    formatMealDay(date) {
      return moment(date).format('ddd');
    },
    async fetchUsage() {
      const dates = this.effectiveDates;
      if (dates.length === 0) {
        return;
      }
      const key = dates.join(',');
      if (key === this.fetchedDatesKey) {
        return;
      }
      this.fetchedDatesKey = key;
      this.loading = true;
      try {
        const {data} = await api.get('v2/meal/component-usage', {params: {dates: key}});
        // ignore stale responses if the selection changed while loading
        if (this.fetchedDatesKey === key) {
          this.meals = data.meals;
          this.components = data.components;
          this.expanded = [];
        }
      } catch (e) {
        console.error('failed to fetch component usage', e);
        alert(`failed to fetch component usage: ${e.message}`);
        this.fetchedDatesKey = null;
      } finally {
        this.loading = false;
      }
    },
    downloadCsv() {
      const rows = this.componentRows.map(r => ({
        component: r.name,
        category: r.category,
        diet_count: r.dietCount,
        diets: r.dietNames.join('; '),
        meal_count: r.mealCount,
        meals: r.mealsByDiet
          .flatMap(g => g.entries.map(e => `${e.meal.name} (${g.dietName} ${e.meal.date})`))
          .join('; '),
      }));
      csvExport(rows, `component-usage-${this.effectiveDates[0]}-${this.effectiveDates[this.effectiveDates.length - 1]}`);
    },
  },
};
</script>

<style scoped>

</style>
